import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';

/**
 * @typedef {Object} VatRefs
 * @property {HTMLFormElement} ValidateVATForm
 */

class VatSufio extends Component {
    /** @type {AbortController | null} */
    #activeFetch = null;

    connectedCallback() {
        super.connectedCallback();
        // No refs required; query within component
    }

    /** @param {SubmitEvent} event */
    validate = async (event) => {
        event.preventDefault();
        const form = event.target;
        if (!(form instanceof HTMLFormElement)) return;

        const emailInput = form.querySelector('[data-vat-email]');
        const vatInput = form.querySelector('[data-vat-number]');
        const successEl = this.querySelector('[data-vat-success]');
        const errorEl = this.querySelector('[data-vat-error]');
        const extraEl = this.querySelector('[data-vat-extra]');
        const closeBtn = this.querySelector('[data-vat-close-btn]');
        const validateBtn = this.querySelector('[data-vat-validate-btn]');

        if (!(emailInput instanceof HTMLInputElement) || !(vatInput instanceof HTMLInputElement)) return;

        const vatNumber = vatInput.value.trim();
        const email = emailInput.value.trim();
        if (!vatNumber || !email) return;

        successEl?.classList.add('hidden');
        errorEl?.classList.add('hidden');

        // VAT check
        const abortController = this.#createAbortController();
        try {
            const vatCheckUrl = `/apps/sufio/vat-check/?number=${encodeURIComponent(vatNumber)}`;
            const vatRes = await fetch(vatCheckUrl, { method: 'GET', mode: 'cors', signal: abortController.signal });
            const vatJson = await vatRes.json();

            const reasonsToCheckout = ['same_country', 'country_not_exempt'];
            const vatOk = Boolean(vatJson?.result) || reasonsToCheckout.includes(vatJson?.reason);

            if (!vatOk) {
                errorEl?.classList.remove('hidden');
                return;
            }

            // Associate VAT with customer
            validateBtn?.setAttribute('aria-disabled', 'true');
            const updateUrl = `/apps/sufio/customer-vat/?email=${encodeURIComponent(email)}&vat_no=${encodeURIComponent(
                vatNumber
            )}&update=true`;

            // Endpoint expects a GET request. Using POST here returns a Shopify "Session expired" HTML page.
            const res = await fetch(updateUrl, { method: 'GET', mode: 'cors', signal: abortController.signal });
            if (!res.ok) throw new Error('Failed to update VAT');
            await res.json().catch(() => ({}));

            successEl?.classList.remove('hidden');
            errorEl?.classList.add('hidden');
            validateBtn?.classList.add('hidden');
            closeBtn?.classList.remove('hidden');
        } catch (e) {
            errorEl?.classList.remove('hidden');
            if (extraEl) {
                extraEl.textContent = `${e}`;
                setTimeout(() => (extraEl.textContent = ''), 3000);
            }
        } finally {
            this.#activeFetch = null;
            validateBtn?.removeAttribute('aria-disabled');
        }
    };

    reset = () => {
        const form = this.querySelector('[data-validate-vat-form]');
        const emailInput = this.querySelector('[data-vat-email]');
        const vatInput = this.querySelector('[data-vat-number]');
        const successEl = this.querySelector('[data-vat-success]');
        const errorEl = this.querySelector('[data-vat-error]');
        const closeBtn = this.querySelector('[data-vat-close-btn]');
        const validateBtn = this.querySelector('[data-vat-validate-btn]');
        if (emailInput instanceof HTMLInputElement) emailInput.value = '';
        if (vatInput instanceof HTMLInputElement) vatInput.value = '';
        successEl?.classList.add('hidden');
        errorEl?.classList.add('hidden');
        closeBtn?.classList.add('hidden');
        validateBtn?.classList.remove('hidden');
        if (form instanceof HTMLFormElement) form.reset();
    };

    #createAbortController() {
        if (this.#activeFetch) this.#activeFetch.abort();
        const ac = new AbortController();
        this.#activeFetch = ac;
        return ac;
    }
}

if (!customElements.get('vat-sufio-component')) {
    customElements.define('vat-sufio-component', VatSufio);
}

